import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UtilsService } from "./utils.service";
import moment from "moment/moment";
import { AuthenticationService } from "./authentication.service";
import { IAddressBook } from "../models/address-book";

interface IUsersForCommercial {
  hash: string;
  complete_name: string;
  firstname: string;
  lastname: string;
  avatar: string;
  role_id: number;
  role: string;
  type: number;
  type_code: string;
  type_name: string;
  contract_start_date: string;
  contract_end_date: null | string;
  cost_total_daily: number;
}

@Injectable({ providedIn: "root" })
export class UserService {
  public types: Array<string> = ["employee", "candidate", "external", "collaborator"];
  public civil_status: any = ["Celibe", "Nubile", "Coniugato/a", "Vedovo/a", "Divorziato/a"];
  public allUserList: {
    hash: string;
    complete_name: string;
    firstname: string;
    lastname: string;
    avatar?: string | null;
    role_id: number;
    role: string;
    type: number;
    type_code: string;
    type_name: string;
    contract_start_date: string;
    contract_end_date: string;
    cost_total_daily: number;
  }[] = [];
  public recruiter_list: {
    hash: string;
    complete_name: string;
  }[] = [];
  constructor(
    private _httpClient: HttpClient,
    private _utilService: UtilsService,
    private _authenticationService: AuthenticationService,
  ) {}

  async getList(type: string = "employee", role: any = null, complete: any = null, fields: string = "avatar,firstname,lastname,complete_name,hash,figure,working_city") {
    return this._httpClient.get(`/user?type=${type}&fields=${fields}${role ? "&role=" + role : ""}${complete ? "&complete=1" : ""}`).toPromise();
  }

  //Lista che usiamo in offerte e commesse
  async getAllUsers(): Promise<Array<IUsersForCommercial>> {
    if (
      this._authenticationService.hasCapability("create_commission") ||
      this._authenticationService.hasCapability("edit_commission") ||
      this._authenticationService.hasCapability("create_deal") ||
      this._authenticationService.hasCapability("edit_deal")
    ) {
      this.allUserList = await this._httpClient
        .get<Array<IUsersForCommercial>>("/user/commercial-list")
        .toPromise()
        .then((response: any) => response || []);
    } else {
      return [];
    }
  }

  //Contiene anche i collaboratori
  async getActiveUsers() {
    return this._httpClient.get("/users-for-lists").toPromise();
  }

  async getExEmployees(year: number = null) {
    if (!year) {
      return this._httpClient.get("/user/ex-employee").toPromise();
    } else {
      return this._httpClient.get(`/user/ex-employee?year=${year}`).toPromise();
    }
  }

  async get(hash: any) {
    return this._httpClient
      .get(`/user/${hash}`)
      .toPromise()
      .then((result: any) => {
        if (result) {
          result.notes = result.notes || [];
          result.notes_contract = result.notes
            .filter((item: any) => item.type === "contract")
            .map((item: any) => {
              item.note = item.note.replace(/⇧/gi, "➔");
              item.note = item.note.replace(/&#8679;/gi, "➔");
              return item;
            });
          result.notes_recruitment = result.notes.filter((item: any) => item.type === "recruitment");
          result.notes = result.notes.filter((item: any) => item.type === "employee");
          return result;
        }
        return false;
      });
  }

  async getMe() {
    return this._httpClient
      .get("/me")
      .toPromise()
      .then((response: any) => {
        return response;
      });
  }

  async getDesiredContract(hash: any) {
    return this._httpClient.get(`/user/${hash}/desired-contract`).toPromise();
  }

  async create(data: any = {}) {
    return this._httpClient
      .post("/user", data)
      .toPromise()
      .then((response) => {
        this.getAllUsers();
        return response;
      })
      .catch((error) => {
        console.error(error);
        throw new Error(error.message);
      });
  }

  async calculateDistanceForEmployeeToUs(hash: string, data: any = {}) {
    return this._httpClient
      .post("/distance-from-us/" + hash, data)
      .toPromise()
      .catch((error) => {
        console.error(error);
        throw new Error(error.message);
      });
  }

  async update(hash: string, data: any = {}) {
    return this._httpClient
      .put(`/user/${hash}`, data)
      .toPromise()
      .then((response) => {
        this.getAllUsers();
        return response;
      });
  }

  async updateEmployeeContract(hash: string, data: any = {}, queryParams: string = "") {
    return this._httpClient
      .put(`/user/${hash}/contract${queryParams}`, data)
      .toPromise()
      .then((response) => {
        this.getAllUsers();
        return response;
      });
  }

  async updateEmployeeResignation(hash: string, data: any = {}) {
    return this._httpClient.put(`/user/${hash}/resignation`, data).toPromise();
  }

  async updateEmployeeAbsences(hash: string, data: any = {}) {
    return this._httpClient.put(`/user/${hash}/absences`, data).toPromise();
  }

  async delete(hash: string) {
    return this._httpClient
      .delete(`/user/${hash}`)
      .toPromise()
      .then((response) => {
        this.getAllUsers();
        return response;
      });
  }

  async change_password(hash: string, data: any = {}) {
    return this._httpClient.post(`/auth/change-password/${hash}`, data).toPromise();
  }

  async generateCV(hash: string) {
    this._utilService.showLoading();
    return this.generateEmployeeCV(hash, "branded");
  }

  async generateCVWithData(hash: string) {
    this._utilService.showLoading();
    return this.generateEmployeeCV(hash, "personal");
  }

  private async generateEmployeeCV(hash: string, type: string) {
    const url: string = type === "personal" ? `/genera-cv-personale/${hash}` : `/genera-cv-aziendale/${hash}`;
    return this._httpClient
      .get(url)
      .toPromise()
      .then(async (response: any) => {
        this._utilService.hideLoading();
        if (response.length > 0) {
          return this._utilService.showSWALError({ text: "ERROR_GENERATE_CV" });
        }
        return this._utilService.showSWALSuccess({ text: "SUCCESS_GENERATE_CV" }).then(async () => {
          await this._utilService.getFile(response.url);
        });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_GENERATE_CV" });
      });
  }

  async sendPassword(data: any) {
    return this._httpClient.post("/set-password", data).toPromise();
  }

  async resetPassword(data: any) {
    return this._httpClient.post("/reset-password", data).toPromise();
  }

  async revokeResignation(hash: string) {
    return this._httpClient.put(`/revoke-resignation/${hash}`, {}).toPromise();
  }

  async restoreEmployee(hash: string) {
    return this._httpClient.put(`/user/restore/${hash}`, {}).toPromise();
  }


  //Crea nota per il dipendente
  async createNote(data: any = {}) {
    return this._httpClient.post("/note", data).toPromise();
  }

  async deleteNote(hash: string, id: string) {
    return this._httpClient.delete("/note/" + id + "/" + hash).toPromise();
  }

  // modifica la nota del dipendente
  async updateNote(hash: string, id: string, data: any = {}) {
    return this._httpClient.put("/note/" + id + "/" + hash, data).toPromise();
  }

  // dipendenti futuri
  async getFutureEmployees() {
    return this._httpClient.get("/user/future-employee").toPromise();
  }

  async getFutureResignedEmployees() {
    return this._httpClient.get("/user/future-ex_employee").toPromise();
  }

  async uploadAvatar(user_hash: string, file: string) {
    return this._httpClient.post(`/user/avatar/${user_hash}`, { file }).toPromise();
  }

  async sendEmailCredentials(hash: string) {
    return this._httpClient.get(`/user/mail-credential/${hash}`).toPromise();
  }

  async addressBook() {
    return this._httpClient.get<IAddressBook[]>("/user/address-book").toPromise();
  }

  async getCommissions(hash: string, month: number = 0, year: number = 0) {
    month = month > 0 ? month : moment().month() + 1;
    year = year > 0 ? year : moment().year();
    return this._httpClient.get(`/commission/active/${hash}/${month}/${year}`).toPromise();
  }

  async deleteEmployeeAttachments(id: string, user_hash: string) {
    return this._httpClient.delete(`/employee/contract-attachment/${user_hash}/${id}`).toPromise();
  }

  async sendMultipleEmail(type: string, data: { users: string[] }) {
    return this._httpClient.post(`/user/send-massive-welcome-emails/${type}`, data).toPromise();
  }

  async getUserPlanning() {
    return this._httpClient.get(`/user-for-planning`).toPromise();
  }

  async getUserEconomicGraph(hash: string, year: number = moment().year()) {
    return this._httpClient.get(`/user/economic-graphic/${hash}/${year}`).toPromise();
  }

  async updateEmployeeCompanyEmail(hash: string, email: string) {
    return this._httpClient.put(`/user/company-email/${hash}`, { email }).toPromise();
  }

  async getRecruiterList() {
    return this._httpClient.get('/user/role/recruiter').toPromise()
      .then((result: any) => {
        this.recruiter_list = result;
        return result;
      });
  }

  async getCommercialList() {
    return this._httpClient.get('/user/role/commercial').toPromise()
      .then((result: any) => {
        this.recruiter_list = result;
        return result;
      });
  }
}
